import axios from 'axios';

// Configuración base de la API
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';

const api = axios.create({
    baseURL: `${API_BASE_URL}/api/v1`,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',

    },
});

// Interceptor para agregar token de autenticación cuando esté disponible
api.interceptors.request.use((config) => {
    const authStorage = localStorage.getItem('auth-storage');
    if (authStorage) {
        const { state } = JSON.parse(authStorage);
        if (state?.tokens?.access_token) {
            config.headers.Authorization = `Bearer ${state.tokens.access_token}`;
        }
    }
    return config;
});

// Tipos para productos
export interface ProductImage {
    id: number;
    product_id: number;
    image_url: string;
    alt_text?: string;
    sort_order: number;
    is_primary: boolean;
    created_at: string;
}

export interface ProductVariant {
    id: number;
    product_id: number;
    sku: string;
    name: string;
    price?: number;
    compare_price?: number;
    cost_price?: number;
    stock_quantity: number;
    weight?: number;
    image_url?: string;
    attributes: Record<string, any>;
    is_active: boolean;
    sort_order: number;
    created_at: string;
    updated_at: string;
}

export interface ProductAttribute {
    id: number;
    name: string;
    type: 'text' | 'number' | 'select' | 'boolean';
    is_required: boolean;
    is_filterable: boolean;
    is_variant: boolean;
    created_at: string;
}

export interface ProductAttributeValue {
    id: number;
    product_id: number;
    attribute_id: number;
    value: string;
    created_at: string;
    attribute: ProductAttribute;
}

export interface Category {
    id: number;
    name: string;
    slug: string;
    description?: string;
    parent_id?: number | null;
    image_url?: string;
    image?: string;
    is_active: boolean;
    sort_order: number;
    created_at: string;
    updated_at: string;
    children?: Category[];
    parent?: Category;
    products_count?: number;
}

export interface Product {
    id: number;
    name: string;
    slug: string;
    description: string;
    short_description?: string;
    sku: string;
    price: number;
    compare_price?: number | null;
    cost_price?: number;
    stock_quantity: number;
    weight?: number;
    dimensions?: string;
    category_id: number;
    brand?: string;
    status: 'active' | 'inactive' | 'draft';
    is_featured: boolean;
    meta_title?: string;
    meta_description?: string;
    tags?: string;
    created_at: string;
    updated_at: string;
    seller_id?: number | undefined;
    view_count?: number;
    in_stock?: boolean;

    // Relaciones
    category: Category;
    images: ProductImage[];
    variants: ProductVariant[];
    attributes: ProductAttributeValue[];

    // Campos calculados
    average_rating?: number;
    reviews_count?: number;
    has_variants: boolean;
    primary_image?: ProductImage | string;
}

export interface ProductFilters {
    category_id?: number;
    min_price?: number;
    max_price?: number;
    brand?: string;
    attributes?: Record<string, string>;
    is_featured?: boolean;
    status?: string;
    search?: string;
    sort?: 'name_asc' | 'name_desc' | 'price_asc' | 'price_desc' | 'created_desc' | 'created_asc';
    page?: number;
    per_page?: number;
}

export interface ProductsResponse {
    data: Product[];
    meta: {
        current_page: number;
        from: number;
        last_page: number;
        per_page: number;
        to: number;
        total: number;
    };
    links: {
        first: string;
        last: string;
        prev?: string;
        next?: string;
    };
}

// Tipos para la respuesta real del backend
export interface BackendProductsResponse {
    success: boolean;
    data: BackendProduct[];
    pagination: {
        current_page: number;
        per_page: number;
        total: number;
        total_pages: number;
        has_more: boolean;
    };
}

export interface BackendProduct {
    id: number;
    name: string;
    slug: string;
    price: number;
    compare_price?: number | null;
    rating_average: number;
    rating_count: number;
    primary_image: string;
    category_name: string;
    seller_name: string;
    in_stock: boolean;
    discount_percentage: number;
}

export interface Review {
    id: number;
    product_id: number;
    user_id: number;
    order_item_id?: number;
    rating: number;
    title?: string;
    comment?: string;
    is_verified: boolean;
    is_approved: boolean;
    helpful_count: number;
    created_at: string;
    updated_at: string;
    user: {
        id: number;
        first_name: string;
        last_name: string;
        profile_image?: string;
    };
}
export interface SellerProfile {
    id: number;
    user_id: number;
    store_name: string;
    store_description?: string;
    verification_status: 'pending' | 'verified' | 'rejected';
    created_at: string;
    updated_at: string;
}
export interface CreateProductData {
    name: string;
    description: string;
    category_id: number;
    sku: string;
    price: number;
    compare_price?: number;
    stock_quantity: number;
    weight?: number;
    dimensions?: string;
    brand?: string;
    tags?: string;
    status?: 'draft' | 'active' | 'inactive';
    is_featured?: boolean;
    meta_title?: string;
    meta_description?: string;
}
// Funciones adaptadoras para transformar respuestas del backend
const adaptBackendProduct = (backendProduct: BackendProduct): Product => {
    // Crear una imagen por defecto si no hay primary_image
    const defaultImage = {
        id: 1,
        product_id: backendProduct.id,
        image_url: backendProduct.primary_image || '',
        alt_text: backendProduct.name,
        sort_order: 0,
        is_primary: true,
        created_at: new Date().toISOString(),
    };

    return {
        id: backendProduct.id,
        name: backendProduct.name,
        slug: backendProduct.slug,
        description: '', // No viene en la respuesta del backend para listings
        short_description: '',
        sku: `SKU-${backendProduct.id}`,
        price: backendProduct.price,
        compare_price: backendProduct.compare_price || null,
        cost_price: 0,
        stock_quantity: backendProduct.in_stock ? 10 : 0, // Valor por defecto
        weight: 0,
        dimensions: '',
        category_id: 1, // Valor por defecto
        brand: backendProduct.seller_name || '',
        status: 'active',
        is_featured: backendProduct.discount_percentage > 20, // Los productos con descuento alto son "destacados"
        meta_title: backendProduct.name,
        meta_description: `${backendProduct.name} - ${backendProduct.category_name}`,
        tags: '',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),

        // Relaciones adaptadas
        category: {
            id: 1, // Por defecto
            name: backendProduct.category_name,
            slug: backendProduct.category_name.toLowerCase()
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '')
                .replace(/[^a-z0-9\s-]/g, '')
                .replace(/\s+/g, '-'),
            description: '',
            parent_id: null,
            image_url: '',
            is_active: true,
            sort_order: 0,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
        },
        images: backendProduct.primary_image ? [defaultImage] : [],
        variants: [],
        attributes: [],

        // Campos calculados
        average_rating: backendProduct.rating_average || 0,
        reviews_count: backendProduct.rating_count || 0,
        has_variants: false,
        primary_image: backendProduct.primary_image ? defaultImage : undefined,
    };
};

const adaptBackendProductsResponse = (backendResponse: BackendProductsResponse): ProductsResponse => {
    const pagination = backendResponse.pagination;

    // Calcular 'from' y 'to' basado en la paginación
    const from = pagination.total > 0 ? ((pagination.current_page - 1) * pagination.per_page) + 1 : 0;
    const to = Math.min(pagination.current_page * pagination.per_page, pagination.total);

    return {
        data: backendResponse.data.map(adaptBackendProduct),
        meta: {
            current_page: pagination.current_page,
            from: from,
            last_page: pagination.total_pages,
            per_page: pagination.per_page,
            to: to,
            total: pagination.total,
        },
        links: {
            first: `?page=1`,
            last: `?page=${pagination.total_pages}`,
            prev: pagination.current_page > 1 ? `?page=${pagination.current_page - 1}` : undefined,
            next: pagination.has_more ? `?page=${pagination.current_page + 1}` : undefined,
        },
    };
};
// Servicio de productos
export const productsService = {
    // Obtener lista de productos con filtros
    async getProducts(filters: ProductFilters = {}): Promise<ProductsResponse> {
        const response = await api.get('/products', { params: filters });

        // Si la respuesta tiene el formato del backend, adaptarla
        if (response.data.pagination) {
            return adaptBackendProductsResponse(response.data);
        }

        // Si ya tiene el formato esperado, devolverla tal como está
        return response.data;
    },

    // Obtener productos destacados
    async getFeaturedProducts(limit: number = 12): Promise<Product[]> {
        const response = await api.get('/products/featured', {
            params: { per_page: limit }
        });

        // Si es array directo, devolverlo
        if (Array.isArray(response.data.data)) {
            return response.data.data.map((product: any) => {
                // Si viene en formato backend, adaptarlo
                if (product.primary_image !== undefined) {
                    return adaptBackendProduct(product);
                }
                return product;
            });
        }

        return response.data.data;
    },

    // Obtener producto por slug
    async getProductBySlug(slug: string): Promise<Product> {
        const response = await api.get(`/products/${slug}`);
        return response.data.data;
    },

    // Obtener producto por ID
    async getProductById(id: number): Promise<Product> {
        const response = await api.get(`/products/${id}`);
        return response.data.data;
    },

    // Obtener productos relacionados
    async getRelatedProducts(productId: number, limit: number = 8): Promise<Product[]> {
        const response = await api.get(`/products/${productId}/related`, {
            params: { limit }
        });
        return response.data.data;
    },

    // Buscar productos
    async searchProducts(query: string, filters: Omit<ProductFilters, 'search'> = {}): Promise<ProductsResponse> {
        const response = await api.get('/products/search', {
            params: { q: query, ...filters }
        });

        // Si la respuesta tiene el formato del backend, adaptarla
        if (response.data.pagination) {
            return adaptBackendProductsResponse(response.data);
        }

        return response.data;
    },

    // Obtener categorías - usando endpoints reales
    async getCategories(includeInactive: boolean = false, flat: boolean = false): Promise<Category[]> {
        const params: any = {};
        if (includeInactive) params.include_inactive = 'true';
        if (flat) params.flat = 'true';

        const response = await api.get('/categories', { params });
        return response.data.data;
    },

    // Obtener categorías populares
    async getPopularCategories(limit: number = 10): Promise<Category[]> {
        const response = await api.get('/categories/popular', {
            params: { limit }
        });
        return response.data.data;
    },

    // Obtener categoría por ID (no slug en el backend)
    async getCategoryById(id: number): Promise<{
        category: any;
        subcategories: any[];
        products: any[];
        stats: any;
    }> {
        const response = await api.get(`/categories/${id}`);

        // El backend ahora devuelve toda la información en response.data.data
        const { category, subcategories, products, stats } = response.data.data;

        console.log('Category API response:', response.data.data);

        return {
            category,
            subcategories,
            products,
            stats
        };
    },

    // Buscar categorías
    async searchCategories(query: string): Promise<Category[]> {
        const response = await api.get('/categories/search', {
            params: { q: query }
        });
        return response.data.data;
    },

    // Obtener productos de una categoría - usando ID en lugar de slug
    async getProductsByCategory(categoryId: number, filters: Omit<ProductFilters, 'category_id'> = {}): Promise<ProductsResponse> {
        const response = await api.get(`/products/category/${categoryId}`, {
            params: filters
        });

        console.log('Raw backend response:', response.data);

        // Adaptar la respuesta del backend al formato esperado
        if (response.data.pagination) {
            const adaptedResponse = adaptBackendProductsResponse(response.data);
            console.log('Adapted response:', adaptedResponse);
            return adaptedResponse;
        }

        return response.data;
    },

    // Obtener reseñas de un producto
    async getProductReviews(productId: number, page: number = 1, perPage: number = 10): Promise<{
        data: Review[];
        meta: any;
    }> {
        const response = await api.get(`/products/${productId}/reviews`, {
            params: { page, per_page: perPage }
        });
        return response.data;
    },

    // Obtener estadísticas de reseñas
    async getProductReviewStats(productId: number): Promise<{
        average_rating: number;
        total_reviews: number;
        rating_distribution: Record<string, number>;
    }> {
        const response = await api.get(`/products/${productId}/reviews/stats`);
        return response.data.data;
    },

    // Crear reseña (requiere autenticación)
    async createReview(productId: number, data: {
        rating: number;
        title?: string;
        comment?: string;
        order_item_id?: number;
    }): Promise<Review> {
        const response = await api.post(`/products/${productId}/reviews`, data);
        return response.data.data;
    },

    // Marcar reseña como útil
    async markReviewHelpful(reviewId: number): Promise<void> {
        await api.post(`/reviews/${reviewId}/helpful`);
    },

    // Obtener variante específica
    async getVariant(variantId: number): Promise<ProductVariant> {
        const response = await api.get(`/variants/${variantId}`);
        return response.data.data;
    },

    // Verificar disponibilidad de stock
    async checkProductStock(productId: number, variantId?: number): Promise<{
        available: boolean;
        stock_quantity: number;
    }> {
        const params = variantId ? { variant_id: variantId } : {};
        const response = await api.get(`/products/${productId}/stock`, { params });
        return response.data.data;
    },
    //UHMM NUEVOS METODOS

    // Crear nuevo producto
    async createProduct(productData: CreateProductData): Promise<Product> {
        const response = await api.post('/products', productData);
        return response.data.data;
    },

    // Actualizar producto existente
    async updateProduct(productId: number, productData: Partial<CreateProductData>): Promise<Product> {
        const response = await api.put(`/products/${productId}`, productData);
        return response.data.data;
    },

    // Eliminar producto
    async deleteProduct(productId: number): Promise<void> {
        await api.delete(`/products/${productId}`);
    },

    // Subir imágenes de producto
    async uploadProductImages(productId: number, images: File[]): Promise<ProductImage[]> {
        const formData = new FormData();
        images.forEach((image, index) => {
            formData.append(`images[${index}]`, image);
        });

        const response = await api.post(`/products/${productId}/images`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });

        return response.data.data;
    },

    // Obtener productos del vendedor (mis productos)
    async getMyProducts(filters: ProductFilters = {}): Promise<ProductsResponse> {
        const response = await api.get('/seller/products', { params: filters });

        // Adaptar respuesta si es necesario
        if (response.data.pagination) {
            return adaptBackendProductsResponse(response.data);
        }

        return response.data;
    },

    // === MÉTODOS AUXILIARES ===

    // Generar SKU único
    generateSKU(productName: string, categoryId: number): string {
        const cleanName = productName
            .replace(/[^a-zA-Z0-9]/g, '')
            .substring(0, 6)
            .toUpperCase();
        const timestamp = Date.now().toString().slice(-6);
        return `${cleanName}-${categoryId}-${timestamp}`;
    },

    // Validar imágenes
    validateImages(files: FileList | File[]): { valid: boolean; errors: string[] } {
        const errors: string[] = [];
        const maxSize = 5 * 1024 * 1024; // 5MB
        const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
        const maxFiles = 10;

        const fileArray = Array.from(files);

        if (fileArray.length > maxFiles) {
            errors.push(`Máximo ${maxFiles} imágenes permitidas`);
        }

        fileArray.forEach((file, index) => {
            if (!allowedTypes.includes(file.type)) {
                errors.push(`Imagen ${index + 1}: Tipo no permitido. Use JPG, PNG o WebP`);
            }

            if (file.size > maxSize) {
                errors.push(`Imagen ${index + 1}: Tamaño máximo 5MB`);
            }
        });

        return {
            valid: errors.length === 0,
            errors
        };
    },

    //ESTOS DEBEN ESTAR EN UN USER SERICE APARTE, LUEGO LOS LLEVO
    async registerAsSeller(storeData: { store_name: string; store_description?: string }): Promise<SellerProfile> {
        const response = await api.post('/seller/register', storeData);
        return response.data.data;
    },
    // Obtener perfil de vendedor
    async getSellerProfile(): Promise<SellerProfile> {
        const response = await api.get('/seller/profile');
        return response.data.data;
    },

    // Actualizar perfil de vendedor
    async updateSellerProfile(data: { store_name: string; store_description?: string }): Promise<SellerProfile> {
        const response = await api.put('/seller/profile', data);
        return response.data.data;
    },
};

// Registrarse como vendedor

export default productsService;